import { ed25519, x25519 } from "@noble/curves/ed25519.js";
import { hkdf } from "@noble/hashes/hkdf.js";
import { sha256 } from "@noble/hashes/sha256.js";
import { breacher } from "./generated/protocol.js";

const PROTO_VERSION = 2;
const BUILD_HASH = "p2-secure-alpha";
const HANDSHAKE_VERSION = 1;
const FRAME_VERSION = 2;
const FRAME_ENVELOPE = 1;
const FRAME_CONTROL = 2;
const HEADER_SIZE = 14;
const MAX_FRAMES = 1 << 20;
const MAX_KEY_AGE_MS = 30 * 60_000;
const REKEY_MARGIN_MS = 60_000;
const encoder = new TextEncoder();

export interface SecureEnvelope {
  id?: string;
  event?: string;
  ok: boolean;
  payload?: unknown;
  error?: { code: string; message: string; status: number; retryable: boolean };
  resultHash?: Uint8Array;
}

export interface SessionKeys {
  clientToServer: Uint8Array;
  serverToClient: Uint8Array;
  clientNoncePrefix: Uint8Array;
  serverNoncePrefix: Uint8Array;
  rekeySecret: Uint8Array;
}

export class FrameCipher {
  private key: CryptoKey | null = null;
  private nextSeq = 0n;
  private frames = 0;
  private readonly createdAt = performance.now();

  constructor(
    private readonly rawKey: Uint8Array,
    private readonly noncePrefix: Uint8Array,
    readonly epoch: number,
  ) {}

  async seal(type: number, plaintext: Uint8Array): Promise<Uint8Array> {
    if (this.needsRekey()) throw new Error("frame key rotation required");
    const header = this.header(type, this.nextSeq);
    const result = await crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: toArrayBuffer(this.nonce(this.nextSeq)),
        additionalData: toArrayBuffer(header),
        tagLength: 128,
      },
      await this.cryptoKey(),
      toArrayBuffer(plaintext),
    );
    this.nextSeq++;
    this.frames++;
    return concat(header, new Uint8Array(result));
  }

  async open(frame: Uint8Array, expectedType: number): Promise<Uint8Array> {
    if (frame.length < HEADER_SIZE + 16) throw new Error("encrypted frame is too short");
    if (frame[0] !== FRAME_VERSION || frame[1] !== expectedType)
      throw new Error("encrypted frame metadata mismatch");
    const view = new DataView(frame.buffer, frame.byteOffset, frame.byteLength);
    if (view.getUint32(2, false) !== this.epoch) throw new Error("encrypted frame epoch mismatch");
    const seq = view.getBigUint64(6, false);
    if (seq !== this.nextSeq) throw new Error("encrypted frame sequence mismatch");
    const header = frame.subarray(0, HEADER_SIZE);
    const result = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: toArrayBuffer(this.nonce(seq)),
        additionalData: toArrayBuffer(header),
        tagLength: 128,
      },
      await this.cryptoKey(),
      toArrayBuffer(frame.subarray(HEADER_SIZE)),
    );
    this.nextSeq++;
    this.frames++;
    return new Uint8Array(result);
  }

  shouldRekey(): boolean {
    return (
      this.frames + 1 >= MAX_FRAMES ||
      performance.now() - this.createdAt >= MAX_KEY_AGE_MS - REKEY_MARGIN_MS
    );
  }

  private needsRekey(): boolean {
    return this.frames >= MAX_FRAMES || performance.now() - this.createdAt >= MAX_KEY_AGE_MS;
  }

  private async cryptoKey(): Promise<CryptoKey> {
    if (!this.key)
      this.key = await crypto.subtle.importKey(
        "raw",
        toArrayBuffer(this.rawKey),
        "AES-GCM",
        false,
        ["encrypt", "decrypt"],
      );
    return this.key;
  }

  private header(type: number, seq: bigint): Uint8Array {
    const header = new Uint8Array(HEADER_SIZE);
    const view = new DataView(header.buffer);
    header[0] = FRAME_VERSION;
    header[1] = type;
    view.setUint32(2, this.epoch, false);
    view.setBigUint64(6, seq, false);
    return header;
  }

  private nonce(seq: bigint): Uint8Array {
    const nonce = new Uint8Array(12);
    nonce.set(this.noncePrefix, 0);
    new DataView(nonce.buffer).setBigUint64(4, seq, false);
    return nonce;
  }
}

export class SecureSocket {
  private sendCipher: FrameCipher;
  private receiveCipher: FrameCipher;
  private sendQueue: Promise<void> = Promise.resolve();
  private receiveQueue: Promise<void> = Promise.resolve();
  private receiveError: Error | undefined;

  private constructor(
    private readonly socket: WebSocket,
    private readonly rootKeys: SessionKeys,
    sendCipher: FrameCipher,
    receiveCipher: FrameCipher,
    private readonly messageListener: (envelope: SecureEnvelope) => void,
    closeListener: (error?: Error) => void,
  ) {
    this.sendCipher = sendCipher;
    this.receiveCipher = receiveCipher;
    socket.addEventListener("close", () => closeListener(this.receiveError));
  }

  static async connect(
    url: string,
    messageListener: (envelope: SecureEnvelope) => void,
    closeListener: (error?: Error) => void,
  ): Promise<SecureSocket> {
    const socket = new WebSocket(url, "breacher.v2");
    socket.binaryType = "arraybuffer";
    await waitForOpen(socket);

    const privateKey = x25519.utils.randomPrivateKey();
    const publicKey = x25519.getPublicKey(privateKey);
    const clientNonce = crypto.getRandomValues(new Uint8Array(32));
    const clientHello = breacher.v2.ClientHello.encode({
      handshakeVersion: HANDSHAKE_VERSION,
      ephemeralPublicKey: publicKey,
      nonce: clientNonce,
      protoVer: PROTO_VERSION,
      buildHash: BUILD_HASH,
    }).finish();
    let connected: SecureSocket | undefined;
    const bufferedFrames: unknown[] = [];
    const serverHelloPromise = firstMessageThenHandoff(
      socket,
      (data) => {
        if (connected) connected.enqueueFrame(data);
        else bufferedFrames.push(data);
      },
      5_000,
    );
    socket.send(clientHello);
    const serverHelloBytes = await serverHelloPromise;
    const serverHello = breacher.v2.ServerHello.decode(serverHelloBytes);
    if (
      serverHello.handshakeVersion !== HANDSHAKE_VERSION ||
      serverHello.ephemeralPublicKey.length !== 32 ||
      serverHello.nonce.length !== 32 ||
      serverHello.signature.length !== 64 ||
      serverHello.signingPublicKey.length !== 32
    ) {
      socket.close(4001, "invalid server hello");
      throw new Error("Invalid secure server hello");
    }
    const pinnedKey = configuredServerKey(serverHello.signingPublicKey);
    const transcript = handshakeTranscript(
      publicKey,
      serverHello.ephemeralPublicKey,
      clientNonce,
      serverHello.nonce,
    );
    if (!ed25519.verify(serverHello.signature, transcript, pinnedKey, { zip215: false })) {
      socket.close(4001, "invalid server signature");
      throw new Error("Secure server signature verification failed");
    }
    const shared = x25519.getSharedSecret(privateKey, serverHello.ephemeralPublicKey);
    const keys = deriveKeys(shared, clientNonce, serverHello.nonce, 0);
    connected = new SecureSocket(
      socket,
      keys,
      new FrameCipher(keys.clientToServer, keys.clientNoncePrefix, 0),
      new FrameCipher(keys.serverToClient, keys.serverNoncePrefix, 0),
      messageListener,
      closeListener,
    );
    for (const frame of bufferedFrames) connected.enqueueFrame(frame);
    return connected;
  }

  get isOpen(): boolean {
    return this.socket.readyState === WebSocket.OPEN;
  }

  close(): void {
    this.socket.close(1000, "client close");
  }

  sendEnvelope(request: {
    id: string;
    method: string;
    opId?: string;
    decisionMs?: number;
    clientTickMs?: number;
    runId?: string;
    runOpSeq?: number;
    preStateHash?: Uint8Array;
    payload?: unknown;
  }): Promise<void> {
    const operation = async () => {
      if (!this.isOpen) throw new Error("WebSocket is not open");
      if (this.sendCipher.shouldRekey()) await this.rotateSendKey();
      const encoded = breacher.v2.ClientEnvelope.encode({
        protoVer: PROTO_VERSION,
        buildHash: BUILD_HASH,
        requestId: request.id,
        method: request.method,
        opId: request.opId ?? "",
        decisionMs: request.decisionMs ?? 0,
        clientTickMs: request.clientTickMs ?? 0,
        runId: request.runId ?? "",
        runOpSeq: request.runOpSeq ?? 0,
        preStateHash: request.preStateHash ?? new Uint8Array(),
        payload: encodeWireValue(request.payload ?? {}),
      }).finish();
      this.socket.send(await this.sendCipher.seal(FRAME_ENVELOPE, encoded));
    };
    const result = this.sendQueue.then(operation);
    this.sendQueue = result.catch(() => undefined);
    return result;
  }

  private async rotateSendKey(): Promise<void> {
    const nextEpoch = this.sendCipher.epoch + 1;
    const update = breacher.v2.KeyUpdate.encode({ nextEpoch }).finish();
    this.socket.send(await this.sendCipher.seal(FRAME_CONTROL, update));
    const keys = deriveKeys(
      this.rootKeys.rekeySecret,
      new Uint8Array(32),
      new Uint8Array(32),
      nextEpoch,
    );
    this.sendCipher = new FrameCipher(keys.clientToServer, keys.clientNoncePrefix, nextEpoch);
  }

  private enqueueFrame(data: unknown): void {
    this.receiveQueue = this.receiveQueue
      .then(() => this.handleFrame(data))
      .catch((error: unknown) => {
        this.receiveError = error instanceof Error ? error : new Error("Invalid encrypted frame");
        this.socket.close(4002, "invalid encrypted frame");
      });
  }

  private async handleFrame(data: unknown): Promise<void> {
    if (!(data instanceof ArrayBuffer)) throw new Error("Encrypted WebSocket frame was not binary");
    const frame = new Uint8Array(data);
    if (frame.length < HEADER_SIZE || frame[0] !== FRAME_VERSION)
      throw new Error("Invalid encrypted frame header");
    const type = frame[1];
    const plaintext = await this.receiveCipher.open(frame, type);
    if (type === FRAME_CONTROL) {
      const update = breacher.v2.KeyUpdate.decode(plaintext);
      if (update.nextEpoch !== this.receiveCipher.epoch + 1)
        throw new Error("Invalid key update epoch");
      const keys = deriveKeys(
        this.rootKeys.rekeySecret,
        new Uint8Array(32),
        new Uint8Array(32),
        update.nextEpoch,
      );
      this.receiveCipher = new FrameCipher(
        keys.serverToClient,
        keys.serverNoncePrefix,
        update.nextEpoch,
      );
      return;
    }
    if (type !== FRAME_ENVELOPE) throw new Error("Unknown encrypted frame type");
    const envelope = breacher.v2.ServerEnvelope.decode(plaintext);
    this.messageListener({
      id: envelope.requestId || undefined,
      event: envelope.event || undefined,
      ok: envelope.ok,
      payload: decodeWireValue(envelope.payload),
      error: envelope.error
        ? {
            code: envelope.error.code ?? "request_failed",
            message: envelope.error.message ?? "请求失败",
            status: envelope.error.status ?? 500,
            retryable: envelope.error.retryable ?? false,
          }
        : undefined,
      resultHash: envelope.resultHash,
    });
  }
}

function deriveKeys(
  secret: Uint8Array,
  clientNonce: Uint8Array,
  serverNonce: Uint8Array,
  epoch: number,
): SessionKeys {
  const epochBytes = new Uint8Array(8);
  new DataView(epochBytes.buffer).setBigUint64(0, BigInt(epoch), false);
  const salt = sha256(concat(clientNonce, serverNonce, epochBytes));
  const material = hkdf(sha256, secret, salt, encoder.encode("breacher/frame-keys/v1"), 104);
  return {
    clientToServer: material.slice(0, 32),
    serverToClient: material.slice(32, 64),
    clientNoncePrefix: material.slice(64, 68),
    serverNoncePrefix: material.slice(68, 72),
    rekeySecret: material.slice(72, 104),
  };
}

export function deriveTransportKeys(
  secret: Uint8Array,
  clientNonce: Uint8Array,
  serverNonce: Uint8Array,
  epoch: number,
): SessionKeys {
  return deriveKeys(secret, clientNonce, serverNonce, epoch);
}

function handshakeTranscript(
  clientPublic: Uint8Array,
  serverPublic: Uint8Array,
  clientNonce: Uint8Array,
  serverNonce: Uint8Array,
): Uint8Array {
  const proto = new Uint8Array(4);
  new DataView(proto.buffer).setUint32(0, PROTO_VERSION, false);
  return concat(
    encoder.encode("breacher/hs/v1"),
    Uint8Array.of(HANDSHAKE_VERSION),
    proto,
    encoder.encode(BUILD_HASH),
    Uint8Array.of(0),
    clientPublic,
    serverPublic,
    clientNonce,
    serverNonce,
  );
}

export function transportHandshakeTranscript(
  clientPublic: Uint8Array,
  serverPublic: Uint8Array,
  clientNonce: Uint8Array,
  serverNonce: Uint8Array,
): Uint8Array {
  return handshakeTranscript(clientPublic, serverPublic, clientNonce, serverNonce);
}

function configuredServerKey(advertised: Uint8Array): Uint8Array {
  const configured = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env
    ?.VITE_BREACHER_SERVER_PUBKEY;
  const hostname = globalThis.location?.hostname ?? "127.0.0.1";
  const loopback = ["localhost", "127.0.0.1", "::1"].includes(hostname);
  if (!configured) {
    if (loopback) return advertised;
    throw new Error("Production build is missing VITE_BREACHER_SERVER_PUBKEY");
  }
  const normalized = configured.replaceAll("-", "+").replaceAll("_", "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  const decoded = Uint8Array.from(atob(padded), (value) => value.charCodeAt(0));
  if (decoded.length !== 32 || !equalBytes(decoded, advertised))
    throw new Error("Server signing key does not match the configured pin");
  return decoded;
}

function encodeWireValue(value: unknown): breacher.v2.IWireValue {
  if (value === null || value === undefined) return { nullValue: {} };
  if (typeof value === "boolean") return { boolValue: value };
  if (typeof value === "string") return { stringValue: value };
  if (typeof value === "bigint") {
    const number = Number(value);
    if (!Number.isSafeInteger(number)) throw new Error("Integer is outside the browser safe range");
    return { intValue: number };
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new Error("Non-finite number cannot be encoded");
    return Number.isSafeInteger(value) ? { intValue: value } : { doubleValue: value };
  }
  if (value instanceof Uint8Array) return { bytesValue: value };
  if (Array.isArray(value)) return { listValue: { values: value.map(encodeWireValue) } };
  if (typeof value === "object") {
    const fields: Record<string, breacher.v2.IWireValue> = {};
    for (const [key, item] of Object.entries(value)) {
      if (item !== undefined) fields[key] = encodeWireValue(item);
    }
    return { objectValue: { fields } };
  }
  throw new Error(`Unsupported protocol value: ${typeof value}`);
}

function decodeWireValue(value: breacher.v2.IWireValue | null | undefined): unknown {
  if (!value) return undefined;
  const kind = (value as breacher.v2.WireValue).kind;
  if (kind === "nullValue") return null;
  if (kind === "intValue") return Number(value.intValue);
  if (kind === "doubleValue") return value.doubleValue;
  if (kind === "stringValue") return value.stringValue;
  if (kind === "boolValue") return value.boolValue;
  if (kind === "bytesValue") return value.bytesValue;
  if (kind === "listValue") return value.listValue?.values?.map(decodeWireValue) ?? [];
  if (kind === "objectValue") {
    const result: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value.objectValue?.fields ?? {}))
      result[key] = decodeWireValue(item);
    return result;
  }
  return undefined;
}

function waitForOpen(socket: WebSocket): Promise<void> {
  return new Promise((resolve, reject) => {
    const opened = () => {
      cleanup();
      resolve();
    };
    const failed = () => {
      cleanup();
      reject(new Error("Unable to open secure WebSocket"));
    };
    const cleanup = () => {
      socket.removeEventListener("open", opened);
      socket.removeEventListener("error", failed);
      socket.removeEventListener("close", failed);
    };
    socket.addEventListener("open", opened);
    socket.addEventListener("error", failed);
    socket.addEventListener("close", failed);
  });
}

function firstMessageThenHandoff(
  socket: WebSocket,
  handoff: (data: unknown) => void,
  timeoutMs: number,
): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    let waitingForHello = true;
    const timer = globalThis.setTimeout(
      () => finish(new Error("Secure handshake timed out")),
      timeoutMs,
    );
    const message = (event: MessageEvent) => {
      if (!waitingForHello) {
        handoff(event.data);
        return;
      }
      if (!(event.data instanceof ArrayBuffer)) {
        finish(new Error("Secure handshake response was not binary"));
        return;
      }
      waitingForHello = false;
      globalThis.clearTimeout(timer);
      socket.removeEventListener("close", closed);
      resolve(new Uint8Array(event.data));
    };
    const closed = () => finish(new Error("Connection closed during secure handshake"));
    const finish = (error: Error) => {
      globalThis.clearTimeout(timer);
      socket.removeEventListener("message", message);
      socket.removeEventListener("close", closed);
      reject(error);
    };
    socket.addEventListener("message", message);
    socket.addEventListener("close", closed);
  });
}

function concat(...values: Uint8Array[]): Uint8Array {
  const result = new Uint8Array(values.reduce((total, value) => total + value.length, 0));
  let offset = 0;
  for (const value of values) {
    result.set(value, offset);
    offset += value.length;
  }
  return result;
}

function equalBytes(left: Uint8Array, right: Uint8Array): boolean {
  if (left.length !== right.length) return false;
  let difference = 0;
  for (let index = 0; index < left.length; index++) difference |= left[index] ^ right[index];
  return difference === 0;
}

function toArrayBuffer(value: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(value.byteLength);
  copy.set(value);
  return copy.buffer;
}
